import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, Spinner } from "@/components/ui/card";
import { api } from "@/lib/api";
import { homeFor, useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { applyTheme, loadTelegram, markTelegram, type TgWebApp } from "@/lib/telegram";

type State = "loading" | "no_telegram" | "not_linked" | "error";

/** Mini App kirish nuqtasi: `initData` imzosi backendda tekshiriladi, ulangan foydalanuvchi parolsiz kiradi. */
export function TelegramPage() {
  const { t, setLang } = useT();
  const { completeLogin } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<State>("loading");
  const [botUrl, setBotUrl] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const app = useRef<TgWebApp | null>(null);
  const started = useRef(-1);

  useEffect(() => {
    if (started.current === attempt) return; // StrictMode: bitta urinish — bitta so'rov
    started.current = attempt;
    setState("loading");
    void (async () => {
      const w = await loadTelegram();
      if (!w || !w.initData) {
        try {
          const cfg = (await api.GET("/api/public-config/")).data as
            { bot_username?: string } | undefined;
          if (cfg?.bot_username) setBotUrl(`https://t.me/${cfg.bot_username.replace(/^@/, "")}`);
        } catch {
          /* havola ixtiyoriy */
        }
        return setState("no_telegram");
      }
      app.current = w;
      markTelegram();
      w.ready();
      w.expand();
      applyTheme(w);
      const res = await api.POST("/api/auth/telegram/", { body: { init_data: w.initData } });
      if (res.response.status === 404) return setState("not_linked");
      if (!res.response.ok || !res.data) return setState("error");
      const user = await completeLogin(res.data.access, res.data.refresh);
      if (!user) return setState("error");
      setLang(user.language === "ru" ? "ru" : "uz");
      navigate(homeFor(user.role), { replace: true });
    })().catch(() => setState("error"));
  }, [attempt, completeLogin, navigate, setLang]);

  return (
    <div className="mx-auto flex min-h-screen max-w-md items-center px-4">
      <Card className="w-full text-center">
        {state === "loading" && (
          <div className="flex flex-col items-center gap-3">
            <Spinner className="size-8" />
            <p>{t("tg.loading")}</p>
          </div>
        )}
        {state === "no_telegram" && (
          <div className="flex flex-col items-center gap-3">
            <p>{t("tg.not_in_telegram")}</p>
            {botUrl && (
              <a
                className="inline-flex min-h-11 items-center rounded-lg bg-primary px-4 font-semibold text-primary-foreground"
                href={botUrl}
                target="_blank"
                rel="noreferrer"
              >
                {t("tg.open_bot")}
              </a>
            )}
          </div>
        )}
        {state === "not_linked" && (
          <div className="flex flex-col items-center gap-3">
            <p role="alert">{t("tg.not_linked")}</p>
            <Button onClick={() => app.current?.close()}>{t("tg.close")}</Button>
          </div>
        )}
        {state === "error" && (
          <div className="flex flex-col items-center gap-3">
            <p role="alert">{t("tg.error")}</p>
            <Button onClick={() => setAttempt((n) => n + 1)}>{t("tg.retry")}</Button>
          </div>
        )}
      </Card>
    </div>
  );
}
